import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { withStyles, styled } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
    getCurrencySymbol,
    hasValidZonePricing,
    parseTravelPattern
} from '../helpers/ticket';
import globalStyles from '../styles';

const styles = {
    root: {
        padding: '10px 0',
        borderTop: '1px solid rgba(0, 0, 0, 0.26)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:last-child': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.26)'
        }
    },
    serviceInfo: {
        paddingRight: '10px'
    },
    button: {
        paddingRight: '8px',
        paddingLeft: '8px'
    },
    closeBtn: {
        position: 'absolute',
        right: 8,
        top: 8
    },
    plainText: {
        background: 'none',
        border: 0,
        padding: 0,
        textDecoration: 'underline',
        cursor: 'pointer',
        ...globalStyles.link
    },
    title: {
        padding: '16px 16px'
    },
    zone: {
        borderBottom: '1px solid #ddd',
        padding: '10px 0px'
    },
    zoneTitle: {
        fontWeight: 'bold'
    },
    modalLinks: {
        borderBottom: '1px solid #ddd',
        paddingBottom: 10,
        '& a': globalStyles.link
    }
};

const formatTicketNamePlainText = ticket => {
    let ticketName = `${ticket.type.charAt(0).toUpperCase()}${ticket.type.slice(
        1
    )}`;

    ticketName = ticketName.replace('_', ' ');

    if (ticket.sub_type === 'pm_only') {
        ticketName += ' (PM only)';
    } else if (ticket.sub_type === 'am_only') {
        ticketName += ' (AM Only)';
    } else if (ticket.sub_type === 'custom' && ticket.travel_pattern) {
        ticketName += ` (${parseTravelPattern(ticket.travel_pattern)} only)`;
    }

    return ticket.name ? `${ticketName} ${ticket.name}` : ticketName;
};

const formatTicketName = ticket => {
    let ticketName = `${ticket.type.charAt(0).toUpperCase()}${ticket.type.slice(
        1
    )}`;

    ticketName = ticketName.replace('_', ' ');

    if (ticket.sub_type === 'pm_only') {
        ticketName += ' (PM only)';
    } else if (ticket.sub_type === 'am_only') {
        ticketName += ' (AM Only)';
    } else if (ticket.sub_type === 'custom' && ticket.travel_pattern) {
        ticketName += ` (${parseTravelPattern(ticket.travel_pattern)} only)`;
    }

    return (
        <strong>
            {ticketName} {ticket.name ? ` ${ticket.name}` : ''}
        </strong>
    );
};

const formatTicketPrice = (service, ticket) => {
    let ticketPrice = `${getCurrencySymbol(ticket.currency)}${ticket.price}`;

    if (ticket.pricing_model === 'Zonal' && ticket.zone_prices) {
        const validTicketZoneNames = Object.keys(ticket.zone_prices).filter(
            ticketZone =>
                service.zones.find(
                    serviceZone => serviceZone.name === ticketZone
                ) && ticket.zone_prices[ticketZone] > 0
        );

        const validTicketZonePrices = validTicketZoneNames.map(
            validZoneName => ticket.zone_prices[validZoneName]
        );

        const zonePrices = Object.values(validTicketZonePrices);
        const zoneMax = Math.max(...zonePrices).toFixed(2);
        const zoneMin = Math.min(...zonePrices).toFixed(2);

        ticketPrice = `${getCurrencySymbol(
            ticket.currency
        )}${zoneMin} - ${getCurrencySymbol(ticket.currency)}${zoneMax}`;
    }

    if (ticket.type === 'flexi') {
        ticketPrice += ` (${ticket.default_trips} trips${
            ticket.pricing_model !== 'Zonal'
                ? ` x ${getCurrencySymbol(ticket.currency)}${ticket.trip_price}`
                : ''
        })`;
    }

    return ticketPrice;
};

const formatTicketInfo = (service, ticket) => {
    return (
        <>
            {formatTicketName(ticket)} {formatTicketPrice(service, ticket)}
        </>
    );
};

const formatDirectDebitNumPayments = ticket => {
    if ('payment_type' in ticket && ticket.payment_type === 'dd') {
        let hasDeposit = false;
        let numPayments = ticket.num_monthly_payments;

        if (ticket.pricing_model === 'Zonal') {
            hasDeposit = Object.values(ticket.zone_prices_deposit).some(
                price => price > 0
            );
            numPayments = Object.values(ticket.zone_prices_monthly).some(
                price => price > 0
            )
                ? numPayments
                : 0;
        } else {
            hasDeposit = ticket.price_deposit > 0;
            numPayments = ticket.price_monthly > 0 ? numPayments : 0;
        }

        const totalInstalments = numPayments + (hasDeposit ? 1 : 0);

        return `${totalInstalments} x Direct Debit payment${
            totalInstalments > 1 ? 's' : ''
        }`;
    }

    return '';
};

const formatRemainingTickets = ticket => {
    if (
        'remaining' in ticket &&
        ticket.remaining > 0 &&
        ticket.remaining <= 10
    ) {
        return `Only ${ticket.remaining} left!`;
    }

    if ('remaining' in ticket && ticket.remaining <= 0) {
        return `SOLD OUT!`;
    }

    return '';
};

const buttonLink = (clientSlug, ticket, service) => {
    const ticketYear = ticket.year.replace('/', '-');
    if (!ticket.available) {
        return `/waiting-list/${ticketYear}/${clientSlug}?ticket=${formatTicketNamePlainText(
            ticket
        )}&service=${service.name}`;
    }
    return `/create-ticket/${ticketYear}/${clientSlug}/${service.id}/${ticket.id}`;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2)
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1)
    }
}));

const BootstrapDialogTitle = props => {
    const { classes, children, onClose, ...other } = props;

    return (
        <DialogTitle {...other} className={classes.title}>
            <Box style={{ display: 'flex' }}>
                <Box>{children}</Box>
                <Box style={{ display: 'inline-flex', width: 30 }}>
                    {onClose ? (
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            className={classes.closeBtn}
                        >
                            <Icon>close</Icon>
                        </IconButton>
                    ) : null}
                </Box>
            </Box>
        </DialogTitle>
    );
};

const renderModal = (classes, ticket, serviceZone) => {
    if (ticket.pricing_model !== 'Zonal' || !serviceZone) {
        return (
            <div key={ticket.id} className={classes.zone}>
                <Typography>
                    Initial payment due now:{' '}
                    {getCurrencySymbol(ticket.currency)}
                    {ticket.price_deposit || '0.00'}
                </Typography>
                <Typography>
                    Monthly payments: {getCurrencySymbol(ticket.currency)}
                    {ticket.price_monthly || '0.00'} x{' '}
                    {ticket.num_monthly_payments}
                </Typography>
                <Typography>
                    First monthly payment: 1st{' '}
                    {moment(ticket.monthly_sub_start).format('MMM')}
                </Typography>
                <Typography>
                    Total price: {getCurrencySymbol(ticket.currency)}
                    {ticket.price}
                </Typography>
            </div>
        );
    }

    if (
        ticket.zone_prices &&
        serviceZone.name in ticket.zone_prices &&
        ticket.zone_prices[serviceZone.name] > 0
    ) {
        return (
            <div
                key={`${ticket.id}_${serviceZone.name}`}
                className={classes.zone}
            >
                <Typography className={classes.zoneTitle}>
                    Zone: {serviceZone.name}
                </Typography>
                <Typography>
                    Initial payment due now:{' '}
                    {getCurrencySymbol(ticket.currency)}
                    {ticket.zone_prices_deposit[serviceZone.name] || '0.00'}
                </Typography>
                <Typography>
                    Monthly payments: {getCurrencySymbol(ticket.currency)}
                    {ticket.zone_prices_monthly[serviceZone.name] || '0.00'}
                    {' x '}
                    {ticket.num_monthly_payments}
                </Typography>
                <Typography>
                    First monthly payment: 1st{' '}
                    {moment(ticket.monthly_sub_start).format('MMM')}
                </Typography>
                <Typography>
                    Total price: {getCurrencySymbol(ticket.currency)}
                    {ticket.zone_prices[serviceZone.name]}
                </Typography>
            </div>
        );
    }

    return <Fragment key={`${ticket.id}_${serviceZone.name}`} />;
};

const ServiceTicket = ({ service, clientSlug, ticket, classes }) => {
    const [open, setOpen] = React.useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    if (ticket.pricing_model === 'Zonal') {
        if (!hasValidZonePricing(ticket, service)) {
            return <></>;
        }
    }

    const isFixedReturn =
        ticket.type === 'return' && ticket.sub_type === 'fixed';

    const isSoldOut = 'remaining' in ticket && ticket.remaining <= 0;

    return (
        <div id={`service-ticket-${ticket.id}`} className={classes.root}>
            {ticket.payment_type === 'dd' && (
                <BootstrapDialog
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                    PaperProps={{
                        style: {
                            margin: 16,
                            minWidth: 300
                        }
                    }}
                >
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={handleClose}
                        classes={classes}
                    >
                        {formatTicketName(ticket)}
                    </BootstrapDialogTitle>
                    <DialogContent dividers>
                        <Typography>
                            This ticket can be paid for in instalments using a
                            direct debit mandate.
                        </Typography>
                        {ticket.pricing_model === 'Zonal' && (
                            <>
                                <Typography className={classes.modalLinks}>
                                    {service.timetable_url !== '' && (
                                        <>
                                            <a
                                                href={service.timetable_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Which zone am I?
                                            </a>{' '}
                                            |{' '}
                                        </>
                                    )}
                                    <a
                                        href="https://help.shuttleid.uk/en/articles/7852307-how-payments-work-with-direct-debit"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Direct Debit FAQ
                                    </a>
                                </Typography>
                                {service.zones.map(serviceZone =>
                                    renderModal(classes, ticket, serviceZone)
                                )}
                            </>
                        )}
                        {ticket.pricing_model !== 'Zonal' && (
                            <>
                                <Typography className={classes.modalLinks}>
                                    <a
                                        href="https://help.shuttleid.uk/en/articles/7852307-how-payments-work-with-direct-debit"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Direct Debit FAQ
                                    </a>
                                </Typography>
                                {renderModal(classes, ticket)}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            autoFocus
                            component={Link}
                            to={buttonLink(clientSlug, ticket, service)}
                            variant="outlined"
                            color="primary"
                        >
                            {ticket.available
                                ? 'Buy ticket'
                                : 'Join waiting list'}
                        </Button>
                    </DialogActions>
                </BootstrapDialog>
            )}

            <div
                id={`service-ticket-info-${ticket.id}`}
                className={classes.serviceInfo}
            >
                {isFixedReturn && (
                    <>
                        <Typography variant="body2">
                            {formatTicketName(ticket)}
                        </Typography>
                        <Typography variant="body2">
                            {moment(ticket.start).format('ddd Do MMM')}{' '}
                        </Typography>
                        <Typography variant="body2">
                            {isSoldOut && (
                                <span style={{ color: 'red' }}>
                                    {formatRemainingTickets(ticket)}
                                </span>
                            )}
                            {!isSoldOut && (
                                <>
                                    {formatTicketPrice(service, ticket)}{' '}
                                    <span style={{ color: 'red' }}>
                                        {formatRemainingTickets(ticket)}
                                    </span>
                                </>
                            )}
                        </Typography>
                    </>
                )}

                {!isFixedReturn && (
                    <>
                        <Typography variant="body2">
                            {formatTicketInfo(service, ticket)}
                        </Typography>
                        {formatDirectDebitNumPayments(ticket) !== '' && (
                            <Typography variant="body2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(true);
                                    }}
                                    className={`${classes.plainText} plaintext`}
                                >
                                    {formatDirectDebitNumPayments(ticket)}
                                </button>
                            </Typography>
                        )}
                        <Typography style={{ color: 'red' }} variant="body2">
                            {formatRemainingTickets(ticket)}
                        </Typography>
                    </>
                )}
            </div>
            <div id={`service-ticket-button-${ticket.id}`}>
                <Button
                    variant="outlined"
                    color="primary"
                    className={`${classes.button} buy-btn`}
                    component={Link}
                    to={buttonLink(clientSlug, ticket, service)}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {ticket.available ? 'Buy' : 'Wait list'}
                </Button>
            </div>
        </div>
    );
};

ServiceTicket.propTypes = {
    ticket: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string
    }).isRequired,
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(ServiceTicket);
